import React, { useCallback, useContext, useMemo } from "react";
import { FastAverageColor } from "fast-average-color";
import { useQuery } from "@tanstack/react-query";
import { contrastColor } from "contrast-color";
import { BannerContext } from "../../../context";
import { BannerStyles } from "../../../config";

const baseBackgroundStyle = {
  backgroundPosition: "center",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
};

const DEFAULT_COLORS = { textColor: "#172B4D", buttonType: "standard" };

const averageColor = new FastAverageColor();

const getContrastColor = (color) =>
  contrastColor({
    bgColor: color,
    fgDarkColor: "#172B4D",
    fgLightColor: "#fff",
    defaultColor: "#172B4D",
  });

const getAverageImageColor = async (url) => {
  try {
    const color = await averageColor.getColorAsync(url);
    return color?.hex || "#000";
  } catch (error) {
    return "#000";
  }
};

export const withStyling = (BaseComponent) =>
  function WithStyling(props) {
    const { globalConfig: { bannerStyle, background } = {} } =
      useContext(BannerContext);

    const { data: colorsForImage } = useQuery({
      queryKey: [
        "getBackgroundAverageColor",
        { background: background?.image?.url },
      ],
      enabled:
        bannerStyle === BannerStyles.STYLED && background?.type === "image_bg",
      queryFn: () =>
        getAverageImageColor(background.image.url)
          .then((color) => {
            const imageContrastColor = getContrastColor(color);
            return {
              textColor: imageContrastColor,
              buttonType:
                imageContrastColor === "#172B4D" ? "standard" : "custom",
            };
          })
          .catch(() => DEFAULT_COLORS),
    });

    const colorsForStandard = useMemo(() => {
      if (
        background?.type === "image_bg" ||
        !background ||
        bannerStyle === BannerStyles.SIMPLE
      ) {
        return DEFAULT_COLORS;
      }

      const foundContrastColor = contrastColor({
        bgColor: background.color,
        fgDarkColor: "#172B4D",
        fgLightColor: "#fff",
        defaultColor: "#172B4D",
      });

      return {
        textColor: foundContrastColor,
        buttonType: foundContrastColor === "#172B4D" ? "standard" : "custom",
      };
    }, [background, bannerStyle]);

    const getBackground = useCallback(() => {
      if (bannerStyle === BannerStyles.SIMPLE)
        return { backgroundColor: "#fff" };

      if (background?.type === "solid_color")
        return { backgroundColor: background.color };
      if (background?.type === "image_bg")
        return { backgroundImage: `url("${background.image.url}")` };

      return { backgroundColor: `#fff` };
    }, [background, bannerStyle]);

    const colors =
      bannerStyle === BannerStyles.STYLED && background?.type === "image_bg"
        ? colorsForImage
        : colorsForStandard;

    return (
      <div
        style={{
          maxWidth: "100%",
          borderRadius: "3px",
          ...baseBackgroundStyle,
          ...getBackground(),
        }}
      >
        <BaseComponent {...colors} {...props} />
      </div>
    );
  };
